import { useContext, useEffect, useState, useMemo, useId, useRef } from 'react';
import classNames from 'classnames';
import { ConfigContext } from '../context/config-context';
import { FormContext } from '../context/form-context';

export const Input = ({
	type,
	name,
	id: propID,
	validation,
	onChange,
	value: propValue,
	hideValidationMessages,
	hideValidation,
	z,
	className,
	...props
}) => {
	const generatedID = useId();
	const { availableValidations, inputTypes, requestValidations } = useContext(ConfigContext);
	const { touched, updateInputState, updateInputValue, removeInput } = useContext(FormContext);

	const valueInitialized = useRef(false);

	const [value, setValue] = useState(propValue);
	const [plainValue, setPlainValue] = useState('');

	const [validations, initialized] = useMemo(() => {
		const validationKeys = Object.keys(validation ?? {});
		var parsedValidations = []
		for(const validationKey of validationKeys) {
			if(availableValidations[validationKey]) {
				const validationOpts = [].concat(validation[validationKey]);
				if(validationOpts[0] === true) {
					validationOpts[0] = undefined;
				}
				parsedValidations.push({
					key: validationKey,
					function: availableValidations[validationKey],
					options: validationOpts,
				})
			}
		}

		return parsedValidations.length === validationKeys.length ? [parsedValidations, true] : [false, false];
	}, [availableValidations, validation])

	const validationError = useMemo(() => {
		if(validations.length > 0) {
			for(const validation of validations) {
				const error = validation.function(plainValue, ...validation.options);
				if(!!error) {
					return error;
				}
			}
			
			return false;
		}
		else {
			return false;
		}
	}, [plainValue, validations]);

	useEffect(() => {
		if(validation) {
			requestValidations(Object.keys(validation));
		}
	}, [validation])

	useEffect(() => {
		setValue(propValue);
	}, [propValue])

	useEffect(() => {
		if(name) {
			updateInputValue(name, id, plainValue);
		}
	}, [plainValue, name])

	useEffect(() => {
		if(onChange) {
			if(valueInitialized.current) {
				onChange(value);
			}
			else {
				valueInitialized.current = true;
			}
		}
	}, [value])

	useEffect(() => {
		if(name) {
			updateInputState(name, initialized, validationError);
		}

		return (() => {
			if(name) {
				removeInput(name);
			}
		})
	}, [validationError, initialized, name])

	const plainValueChange = (value) => {
		setPlainValue(value);
	}

	const valueChange = (value) => {
		if(value !== undefined) {
			setValue(value);
		}
	}

	const showError = touched && initialized && !hideValidation && !!validationError;

	const computedClassNames = useMemo(() => {
		return classNames('inputwrap', `type-${type}`, className, { 'loading': !initialized, 'validation-error': showError, [`z-${z}`]: !!z, 'input-full': (value ?? '').toString().length > 0 })
	}, [className, type, initialized, showError, value, z]);

	const id = useMemo(() => {
		return propID ?? `form-input-${name ?? generatedID}`;
	}, [propID, name, generatedID])

	const Input = inputTypes[type];

	if(Input) {
		return (
			<Input
				{...{
					...props,
					initialized,
					value,
					id,
					name,
					inputType: type,
					validationError: (showError && !hideValidationMessages) ? validationError : false,
					className: computedClassNames,
					onChange: valueChange,
					onChangePlainValue: plainValueChange,
				}} />
		)
	}
	else {
		console.warn(`Warning, input type ${type} not found.`);
		return null;
	}
}

Input.defaultProps = {
	type: 'text',
	value: '',
	hideValidation: false,
	hideValidationMessages: false,
}